import { JWMLink } from "@components/JWMLink/JWMLink";
import { FileDownloadImpression } from "@jmc/core/src/components/FileDownloadImpression/index";
import { GatsbyImageCard } from "@jmc/core/src/components/GatsbyImageCard/index";
import { ImageQueryResponse } from "@jmc/core/src/types/ImageQueryReponse";
import { Button } from "@jmc/solid-design-system/src/components/atoms/Button/Button";
import { Icon, jnjDownload, jnjEye } from "@jmc/solid-design-system/src/components/atoms/Icon/Icon";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { ImagePosition } from "@jmc/solid-design-system/src/components/molecules/Card/Card";
import { Html } from "@jmc/solid-design-system/src/components/molecules/Html/Html";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import classnames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";

import style from "./style.module.scss";

interface Props {
    id: string;
    showTitle: boolean;
    showDescription: boolean;
    fileName: string;
    fileUrl: string;
    fileSize: string;
    image?: ImageQueryResponse;
    title?: string | JSX.Element | JSX.Element[];
    description?: string;
    imageFocus: ImagePosition;
    loading?: "eager" | "lazy";
}

export const MaterialsCard = (props: Props): JSX.Element => {
    const {
        id,
        showTitle,
        showDescription,
        fileName,
        fileUrl,
        fileSize,
        image = null,
        imageFocus,
        title = null,
        description = null,
        loading = "lazy",
    } = props;

    const { t } = useTranslation();
    const { jnjFullBranded } = useJnjBranding();

    const fileSizeInKb = fileSize ? parseInt(fileSize) / 1024 : 0;
    const displayFileSize = fileSizeInKb.toFixed(2).toString() + "KB";

    // Contentstack adds the extension to the file, even if no extension is present on the uploaded file
    const fileExt = fileName ? fileName.split(".").pop() : "";
    const isDownloadable = fileExt.toLowerCase() !== "pdf";
    const linkText = isDownloadable ? t("Download", { ns: "media" }) : t("View", { ns: "media" });
    const ariaLabel = title?.toString() || description || linkText;

    const accessoryLeft = (
        <Icon
            data-test-id={isDownloadable ? "download-icon" : "view-icon"}
            className={style.locked}
            icon={isDownloadable ? jnjDownload : jnjEye}
            size={isDownloadable ? "xs" : "small"}
            type="jnj"
        />
    );

    const footer = (
        <div className={classnames(style.materialsCardFooter)}>
            <span className={classnames(style.downloadText)}>
                <JWMLink ariaLabel={ariaLabel} url={fileUrl} external={true}>
                    {jnjFullBranded ? (
                        <Button
                            color="primary-550"
                            variant="naked"
                            size="medium"
                            accessoryLeft={accessoryLeft}
                            flexibleWidth={true}
                        >
                            <Typography color="inherit">{linkText}</Typography>
                        </Button>
                    ) : (
                        <Typography variant="body" color="title">
                            {linkText}
                        </Typography>
                    )}
                </JWMLink>
            </span>
            <span className={classnames(style.fileSize)}>
                <Typography variant="micro" size="s" color="gray">
                    {displayFileSize}
                </Typography>
            </span>
        </div>
    );

    return (
        <FileDownloadImpression name={fileName}>
            <GatsbyImageCard
                data-test-id={`Card.${id}`}
                title={showTitle && title}
                fullBackground={false}
                image={image}
                footer={footer}
                imageFocus={imageFocus}
                idPrefix={"MaterialCard"}
                loading={loading}
                isTransparent={jnjFullBranded}
                isClickable={jnjFullBranded}
            >
                {showDescription && (
                    <Typography color={jnjFullBranded ? "inherit" : "gray"}>
                        <Html color="inherit">{description}</Html>
                    </Typography>
                )}
            </GatsbyImageCard>
        </FileDownloadImpression>
    );
};

export default MaterialsCard;
